import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";

import VideoJS from "../../../atoms/VideoJS";
import Spinner from "../../../atoms/Spinner";
import ModalConfirm from "../../../molecules/ModalConfirm";
import TeachingMaterialModal from "../../../organisms/teacher/TeachingMaterialModal";
import PublishMaterialModal from "../../../organisms/teacher/PublishMaterialModal";
import { chooseTextbookPath, textbookPath } from "../../../teacherStore";
import {
  ITeachingMaterial,
  ILesson,
  ITeachingMaterialModalRect,
} from "../../../state";
import { TEACHER_SESSION_KEY } from "../../../Definition";
import { ReactComponent as IconClose } from "../../../assets/svg/close.svg";
import ChatBubbleModal from "../../../atoms/ChatBubbleModal";
import ChatBubblePage from "../../../pages/teacher/ChatBubblePage";
import ChatBubbleIcon from "../../../atoms/ChatBubbleIcon";
import NotifyHintModalPage from "../../../pages/teacher/NotifyHintModalPage";
import {
  CHAT_BUBBLE_MODAL_PAGES,
  ChatBubbleModalPage,
} from "../../../constants/teaching_hint";
import {
  TEACHING_MATERIAL_KEYWORDS,
  CategoryType,
} from "../../../constants/teaching_materials";
import {
  isOldMaterial,
  getCurrentMaterials,
  resolveActiveTab,
  CategorizedMaterials,
} from "../../../libs/teachingMaterialUtils";
import useCategorizeMaterials from "../../../hooks/useCategorizeMaterials";
import useAppOrderStatus from "../../../hooks/useAppOrderStatus";
import Sidebar from "../../../organisms/teacher/Sidebar";

import PublishControls from "./PublishControls";
import LessonActionGrid from "./LessonActionGrid";
import styles from "./SelectPage.module.scss";

export interface ISelectPageProps {
  teaching_materials: ITeachingMaterial[];
  classList: string[];
  calling: boolean;
  onPublishLesson: (lesson_id: number) => void;
  onUnPublishLesson: (lesson_id: number) => void;
  onPublishMaterial: (teaching_material_id: number) => void;
  onUnPublishMaterial: (teaching_material_id: number) => void;
  openGuideline: (teaching_material_id: number, lesson_id: number) => void;
  openMaterial: (teaching_material_id: number, lesson_id: number) => void;
  openSubPage: (teaching_material_id: number, lesson_id: number) => void;
  teacherSurveyEnabled: boolean;
  isSurveyShown: boolean;
  showNotificationHintModal: boolean;
  onCloseNotificationModal: () => void;
}

const getMaterialsForTab = (
  tab: CategoryType,
  categorizedMaterials: CategorizedMaterials,
  orientation: ITeachingMaterial | undefined,
): ITeachingMaterial[] => {
  const list = getCurrentMaterials(categorizedMaterials, tab);

  const isCoreCategory = categorizedMaterials.hasMultipleMaterialCategories
    ? tab !== "base"
    : categorizedMaterials.hasMiddle ||
      categorizedMaterials.hasHigh ||
      categorizedMaterials.hasCore;
  const isOrientationOnly =
    !categorizedMaterials.hasMiddle &&
    !categorizedMaterials.hasHigh &&
    !categorizedMaterials.hasCore &&
    !categorizedMaterials.hasBase;

  if (orientation && (isCoreCategory || isOrientationOnly)) {
    if (!list.find(m => m.id === orientation.id)) {
      return [orientation, ...list];
    }
  }
  return list;
};

const findDefaultLesson = (
  materials: ITeachingMaterial[],
  targetClassIds: number[],
) => {
  let lastCompletedMaterialId: number | null = null;
  let lastCompletedLessonId: number | null = null;

  for (const material of materials) {
    if (!material.lessons) continue;
    for (const lesson of material.lessons) {
      if (
        !isOldMaterial(material.mitsuwa_id) &&
        lesson.title?.includes("はじめに")
      ) {
        continue;
      }
      const hasEnoughAnswers = lesson.answer_statuses?.some(
        status =>
          targetClassIds.includes(status.klass_id) && status.has_enough_answers,
      );

      if (hasEnoughAnswers) {
        lastCompletedMaterialId = material.id;
        lastCompletedLessonId = lesson.id;
      }
    }
  }

  if (lastCompletedMaterialId !== null && lastCompletedLessonId !== null) {
    return {
      materialId: lastCompletedMaterialId,
      lessonId: lastCompletedLessonId,
    };
  } else if (materials.length > 0) {
    const firstMaterial = materials[0];
    if (firstMaterial.lessons && firstMaterial.lessons.length > 0) {
      const fallbackLesson = !isOldMaterial(firstMaterial.mitsuwa_id)
        ? firstMaterial.lessons.find(l => !l.title?.includes("はじめに"))
        : firstMaterial.lessons[0];
      if (fallbackLesson) {
        return {
          materialId: firstMaterial.id,
          lessonId: fallbackLesson.id,
        };
      }
    }
  }

  return { materialId: null, lessonId: null };
};

export const SelectPage = (props: ISelectPageProps) => {
  const { hasOnlyDlpOrder } = useAppOrderStatus();
  const { pathname, search } = useLocation();
  const location = useLocation<{
    savedTab?: CategoryType;
    savedMaterialId?: number;
    savedLessonId?: number;
  }>();
  const history = useHistory();

  const locationState = useMemo(() => location.state || {}, [location.state]);

  const targetClassIds = useMemo(() => {
    const queryParams = new URLSearchParams(search);
    const classListValue = queryParams.get("classList");
    return classListValue
      ? classListValue.split(",").map(value => Number(value.trim()))
      : [];
  }, [search]);

  const categorizedMaterials = useCategorizeMaterials(props.teaching_materials);

  const orientation = useMemo(
    () =>
      props.teaching_materials.find(tm =>
        tm.title.includes(TEACHING_MATERIAL_KEYWORDS.ORIENTATION),
      ),
    [props.teaching_materials],
  );

  const [userSelectedTab, setUserSelectedTab] = useState<CategoryType>(
    locationState.savedTab ?? "middle",
  );

  const activeTab = useMemo(
    () => resolveActiveTab(categorizedMaterials, userSelectedTab),
    [categorizedMaterials, userSelectedTab],
  );
  const [viewingMaterialId, setViewingMaterialId] = useState<number | null>(
    null,
  );
  const [activeLessonId, setActiveLessonId] = useState<number | null>(null);

  const [activeModalId, setActiveModalId] = useState<number | null>(null);
  const [
    modalTargetRect,
    setModalTargetRect,
  ] = useState<ITeachingMaterialModalRect | null>(null);
  const [confirmStatusLesson, setConfirmStatusLesson] = useState<{
    isShowModal: boolean;
    lesson: ILesson;
    actionCallback: () => void;
  } | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoOptions, setVideoOptions] = useState({
    url: "",
    playing: false,
  });
  const [showChat, setShowChat] = useState<boolean>(!props.isSurveyShown);
  const [modalPage, setModalPage] = useState<ChatBubbleModalPage>(() =>
    pathname.includes("guideline")
      ? CHAT_BUBBLE_MODAL_PAGES.HINTS
      : CHAT_BUBBLE_MODAL_PAGES.SURVEY,
  );
  const [isPublishMaterialModalOpen, setIsPublishMaterialModalOpen] = useState(
    false,
  );

  const mountedRef = useRef(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    if (mountedRef.current) {
      setHasLoaded(true);
    }
    mountedRef.current = true;
  }, [props.teaching_materials]);

  const currentMaterials = useMemo(
    () => getMaterialsForTab(activeTab, categorizedMaterials, orientation),
    [activeTab, categorizedMaterials, orientation],
  );

  useEffect(() => {
    if (currentMaterials.length === 0 || viewingMaterialId !== null) return;
    if (locationState.savedMaterialId && locationState.savedLessonId) {
      const exists = currentMaterials.some(
        m => m.id === locationState.savedMaterialId,
      );
      if (exists) {
        setViewingMaterialId(locationState.savedMaterialId);
        setActiveLessonId(locationState.savedLessonId);
        return;
      }
    }

    const { materialId, lessonId } = findDefaultLesson(
      currentMaterials,
      targetClassIds,
    );
    if (materialId !== null && lessonId !== null) {
      setViewingMaterialId(materialId);
      setActiveLessonId(lessonId);
    }
  }, [currentMaterials, viewingMaterialId, locationState, targetClassIds]);

  useEffect(() => {
    setModalPage(
      pathname.includes("guideline")
        ? CHAT_BUBBLE_MODAL_PAGES.HINTS
        : CHAT_BUBBLE_MODAL_PAGES.SURVEY,
    );
  }, [pathname, setModalPage]);

  useEffect(() => {
    if (viewingMaterialId === null || activeLessonId === null) return;

    history.replace(location.pathname + location.search, {
      savedTab: activeTab,
      savedMaterialId: viewingMaterialId,
      savedLessonId: activeLessonId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, viewingMaterialId, activeLessonId]);

  const handleTabClick = (newTab: CategoryType) => {
    if (activeTab === newTab) return;
    setUserSelectedTab(newTab);
    const nextMaterials = getMaterialsForTab(
      newTab,
      categorizedMaterials,
      orientation,
    );
    const { materialId, lessonId } = findDefaultLesson(
      nextMaterials,
      targetClassIds,
    );

    if (materialId !== null && lessonId !== null) {
      setViewingMaterialId(materialId);
      setActiveLessonId(lessonId);
    }
  };

  const handleItemClick = (
    e: React.MouseEvent<HTMLDivElement>,
    materialId: number,
  ) => {
    const clickedMaterial = currentMaterials.find(m => m.id === materialId);

    const isOrientation = clickedMaterial?.title.includes(
      TEACHING_MATERIAL_KEYWORDS.ORIENTATION,
    );

    if (isOrientation) {
      if (
        clickedMaterial &&
        clickedMaterial.lessons &&
        clickedMaterial.lessons.length > 0
      ) {
        handleSelectLesson(clickedMaterial.lessons[0].id, materialId);
      }
      return;
    }

    if (activeModalId === materialId) {
      setActiveModalId(null);
      setModalTargetRect(null);
    } else {
      setActiveModalId(materialId);
      const rect = e.currentTarget.getBoundingClientRect();
      setModalTargetRect({
        top: rect.top,
        bottom: rect.bottom,
        right: rect.right,
      });
    }
  };

  const handleBackdropClick = () => {
    setActiveModalId(null);
    setModalTargetRect(null);
  };

  const handleOpenVideo = () => {
    const videoUrl = `${process.env.REACT_APP_TEACHER_DLP_URL}${
      hasOnlyDlpOrder
        ? "/movie/CORE2.3/orientation_core_only/orientation_core_only"
        : "/movie/CORE2.3/orientation_all/orientation_all"
    }`;

    setVideoOptions({ url: videoUrl, playing: false });
    setShowVideo(true);
  };

  const handleCloseVideo = () => {
    setVideoOptions(prevState => ({
      ...prevState,
      playing: false,
    }));
    setShowVideo(false);
  };

  const handleCloseHintModal = () => {
    props.onCloseNotificationModal();
  };

  const handleBackToClass = () => {
    history.push(`${textbookPath}${chooseTextbookPath}`);
  };

  const handleSelectLesson = (lessonId: number, materialId: number) => {
    setViewingMaterialId(materialId);
    setActiveLessonId(lessonId);
    setActiveModalId(null);
    setModalTargetRect(null);
  };

  const executeLessonAction = (lesson: ILesson, actionCallback: () => void) => {
    if (!lesson.published && !lesson.teacher_only) {
      setConfirmStatusLesson({
        isShowModal: true,
        lesson,
        actionCallback,
      });
      return;
    }
    actionCallback();
  };

  const activeMaterial = currentMaterials.find(m => m.id === viewingMaterialId);
  const activeLesson = activeMaterial?.lessons?.find(
    l => l.id === activeLessonId,
  );
  const activeModalMaterial = currentMaterials.find(
    m => m.id === activeModalId,
  );

  const extra = sessionStorage.getItem(TEACHER_SESSION_KEY)
    ? `&session_id=${sessionStorage.getItem(TEACHER_SESSION_KEY)}`
    : "";

  const isCurrentOrientation = Boolean(
    activeMaterial?.title.includes(TEACHING_MATERIAL_KEYWORDS.ORIENTATION),
  );

  const isIntroLesson = Boolean(
    activeLesson?.title?.includes("はじめに") ||
      activeLesson?.name?.includes("はじめに"),
  );

  const isBlankNameLesson = Boolean(
    activeLesson && (!activeLesson.name || activeLesson.name.trim() === ""),
  );

  const isBaseCategory = Boolean(
    activeMaterial?.kind?.includes(TEACHING_MATERIAL_KEYWORDS.BASE),
  );

  return (
    <div className={styles.pageLayout}>
      {!hasLoaded ? (
        <div className={styles.loading}>
          <Spinner spinColor="black" />
          <p className={styles.loadingText}>教材取得中...</p>
        </div>
      ) : props.teaching_materials.length === 0 ? (
        <div className={styles.loading}>
          <p className={styles.loadingText}>教材がありません</p>
        </div>
      ) : (
        <>
          {activeModalId !== null && (
            <div className={styles.backdrop} onClick={handleBackdropClick} />
          )}
          <Sidebar
            classList={props.classList}
            currentMaterials={currentMaterials}
            viewingMaterialId={viewingMaterialId}
            activeModalId={activeModalId}
            activeTab={activeTab}
            targetClassIds={targetClassIds}
            showTabs={categorizedMaterials.hasMultipleMaterialCategories}
            hasMiddle={categorizedMaterials.hasMiddle}
            hasHigh={categorizedMaterials.hasHigh}
            hasCore={categorizedMaterials.hasCore}
            hasBase={categorizedMaterials.hasBase}
            onBackToClass={handleBackToClass}
            onTabClick={handleTabClick}
            onItemClick={handleItemClick}
          />
          <main className={styles.mainContent}>
            {activeMaterial && activeLesson && (
              <div className={styles.lessonDetailView}>
                <div className={styles.lessonTopHeader}>
                  <h2 className={styles.lessonMainTitle}>
                    {isCurrentOrientation ? (
                      <>
                        {activeMaterial.title}{" "}
                        <span className={styles.lessonSubTitle}>
                          {activeLesson.title}
                        </span>
                      </>
                    ) : (
                      <>
                        {!activeLesson.title?.startsWith(
                          activeMaterial.name,
                        ) && <>{activeMaterial.name} </>}
                        {activeLesson.name}{" "}
                        <span className={styles.lessonSubTitle}>
                          {activeLesson.title}
                        </span>
                      </>
                    )}
                  </h2>
                  {!isCurrentOrientation && !activeLesson.teacher_only && (
                    <PublishControls
                      activeLesson={activeLesson}
                      calling={props.calling}
                      onPublishLesson={props.onPublishLesson}
                      onUnPublishLesson={props.onUnPublishLesson}
                      onOpenSettingModal={() =>
                        setIsPublishMaterialModalOpen(true)
                      }
                    />
                  )}
                </div>
                <LessonActionGrid
                  activeMaterial={activeMaterial}
                  activeLesson={activeLesson}
                  isCurrentOrientation={isCurrentOrientation}
                  isIntroLesson={isIntroLesson}
                  isBlankNameLesson={isBlankNameLesson}
                  isBaseCategory={isBaseCategory}
                  targetClassIds={targetClassIds}
                  classList={props.classList}
                  extra={extra}
                  onOpenVideo={handleOpenVideo}
                  onOpenGuideline={props.openGuideline}
                  onOpenMaterial={props.openMaterial}
                  onOpenSubPage={props.openSubPage}
                  onExecuteLessonAction={executeLessonAction}
                  onShowChat={() => setShowChat(true)}
                />
              </div>
            )}
            {confirmStatusLesson && confirmStatusLesson.isShowModal && (
              <ModalConfirm
                onClose={() => {
                  !props.calling && setConfirmStatusLesson(null);
                }}
                title={`このLessonは生徒に未公開です。\n公開しますか？`}
                buttons={[
                  {
                    title: `公開せずに開く`,
                    action: () => {
                      if (!props.calling) {
                        setConfirmStatusLesson(null);
                        confirmStatusLesson.actionCallback();
                      }
                    },
                    type: "None",
                  },
                  {
                    title: "公開する",
                    action: () => {
                      if (!props.calling) {
                        props.onPublishLesson(confirmStatusLesson.lesson.id);
                        setConfirmStatusLesson(null);
                      }
                    },
                    type: "Publish",
                  },
                ]}
              />
            )}
            <div
              className={styles.video}
              style={{
                display:
                  showVideo && videoOptions.url.length > 0 ? "block" : "none",
              }}
            >
              <div className={styles.close} onClick={handleCloseVideo}>
                <IconClose />
              </div>
              {!!videoOptions.url && (
                <VideoJS
                  key={videoOptions.url}
                  className={styles.videojs}
                  playing={videoOptions.playing}
                  showVideo={showVideo}
                  sourceUrl={videoOptions.url}
                />
              )}
            </div>
            {props.teacherSurveyEnabled && (
              <>
                {showChat && (
                  <>
                    <ChatBubbleIcon
                      onClose={() => {
                        setShowChat(false);
                        setModalPage(CHAT_BUBBLE_MODAL_PAGES.SURVEY);
                      }}
                      modalPage={modalPage}
                    />
                    <ChatBubbleModal
                      onClose={() => {
                        setShowChat(false);
                        setModalPage(CHAT_BUBBLE_MODAL_PAGES.SURVEY);
                      }}
                      transparentBackDrop={true}
                    >
                      <ChatBubblePage
                        onClose={() => {
                          setShowChat(false);
                          setModalPage(CHAT_BUBBLE_MODAL_PAGES.SURVEY);
                        }}
                        modalPage={modalPage}
                        setModalPage={setModalPage}
                      />
                    </ChatBubbleModal>
                  </>
                )}
                {props.showNotificationHintModal && (
                  <NotifyHintModalPage onClose={handleCloseHintModal} />
                )}
              </>
            )}
          </main>
          {activeModalMaterial && modalTargetRect && (
            <TeachingMaterialModal
              material={activeModalMaterial}
              targetRect={modalTargetRect}
              targetClassIds={targetClassIds}
              onSelectLesson={lessonId =>
                handleSelectLesson(lessonId, activeModalMaterial.id)
              }
            />
          )}
          {isPublishMaterialModalOpen && activeMaterial && (
            <PublishMaterialModal
              material={activeMaterial}
              calling={props.calling}
              onPublishLesson={props.onPublishLesson}
              onUnPublishLesson={props.onUnPublishLesson}
              onPublishMaterial={props.onPublishMaterial}
              onUnPublishMaterial={props.onUnPublishMaterial}
              onClose={() => setIsPublishMaterialModalOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SelectPage;
